import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./GpsMapModal.css";
import ActionButton from "./ActionButton";
import bluePinPng from "../assets/marker-icon.png";
import bluePin2xPng from "../assets/marker-icon-2x.png";
import redPin2xPng from "../assets/marker-icon-2x-red.png";
import markerShadowPng from "../assets/marker-shadow.png";
import { MAP_TILE_ATTRIBUTION, MAP_TILE_MAX_ZOOM, MAP_TILE_URL } from "./projectMapLeaflet";

export interface GpsMapPoint {
  lat: number;
  lng: number;
  label: string;
  isException?: boolean;
  exceptionMessage?: string;
  inRadius?: boolean;
  /** ISO/sortable date for chronological ordering. */
  sortKey?: string;
  /** Project name (and alt ID) to show next to the punch in the list. */
  projectLabel?: string;
}

export interface GpsMapCircle {
  lat: number;
  lng: number;
  radiusMiles: number;
}

export interface GpsMapCircleWithLabel extends GpsMapCircle {
  label?: string;
}

interface GpsMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  points: GpsMapPoint[];
  /** Multiple job buffers (e.g. one per project worked that day). Each can have an optional label for the popup. */
  circles?: GpsMapCircleWithLabel[];
  /** @deprecated Use circles instead. Single circle for backward compatibility. */
  circle?: GpsMapCircle;
  /** @deprecated Use circles[].label instead. */
  circleLabel?: string;
  showRadiusLegend?: boolean;
  /** When true, show a list of punches below the map; clicking one highlights it and focuses that pin (pan + popup). */
  showPunchList?: boolean;
}

const DEFAULT_CENTER: [number, number] = [39.8283, -98.5795];
const DEFAULT_ZOOM = 0;
const SINGLE_POINT_ZOOM = 14;

function isValidCoord(lat: unknown, lng: unknown): boolean {
  return typeof lat === "number" && typeof lng === "number" && !Number.isNaN(lat) && !Number.isNaN(lng);
}

function extendBoundsForCircle(bounds: L.LatLngBounds, circle: GpsMapCircle): void {
  const milesPerDegLat = 69;
  const milesPerDegLng = 69 * Math.cos((circle.lat * Math.PI) / 180);
  const dLat = circle.radiusMiles / milesPerDegLat;
  const dLng = circle.radiusMiles / milesPerDegLng;
  bounds.extend([circle.lat + dLat, circle.lng + dLng]);
  bounds.extend([circle.lat - dLat, circle.lng - dLng]);
}

function FitBounds({
  points,
  circles,
}: {
  points: GpsMapPoint[];
  circles: GpsMapCircleWithLabel[];
}) {
  const map = useMap();
  useEffect(() => {
    const validPoints = points.filter((p) => isValidCoord(p.lat, p.lng));
    const validCircles = circles.filter(
      (c) => isValidCoord(c.lat, c.lng) && typeof c.radiusMiles === "number"
    );
    if (validPoints.length === 0 && validCircles.length === 0) return;

    const bounds = L.latLngBounds(
      validPoints.map((p) => [p.lat, p.lng] as [number, number])
    );
    validCircles.forEach((c) => extendBoundsForCircle(bounds, c));
    map.fitBounds(bounds.pad(0.2), { maxZoom: SINGLE_POINT_ZOOM });
  }, [map, points, circles]);
  return null;
}

// Blue pin marker (Leaflet default style), using local PNG assets
const bluePinIcon = L.icon({
  iconUrl: bluePinPng,
  iconRetinaUrl: bluePin2xPng,
  shadowUrl: markerShadowPng,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// Red pin marker (same shape, for exception punches), using local PNG
const redPinIcon = L.icon({
  iconUrl: redPin2xPng,
  iconRetinaUrl: redPin2xPng,
  shadowUrl: markerShadowPng,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

L.Marker.prototype.options.icon = bluePinIcon;

function FocusSelectedPin({
  selectedPointIndex,
  validPoints,
  markerRefs,
}: {
  selectedPointIndex: number | null;
  validPoints: GpsMapPoint[];
  markerRefs: React.MutableRefObject<(L.Marker | null)[]>;
}) {
  const map = useMap();
  useEffect(() => {
    if (selectedPointIndex == null) return;
    const p = validPoints[selectedPointIndex];
    if (!p) return;
    map.panTo([p.lat, p.lng]);
    const marker = markerRefs.current[selectedPointIndex];
    if (marker && typeof (marker as any).openPopup === "function") (marker as L.Marker).openPopup();
  }, [map, selectedPointIndex, validPoints, markerRefs]);
  return null;
}

const GpsMapModal: React.FC<GpsMapModalProps> = ({
  isOpen,
  onClose,
  title = "Map",
  points,
  circles: circlesProp,
  circle,
  circleLabel,
  showRadiusLegend = false,
  showPunchList = false,
}) => {
  const validPoints = useMemo(() => points.filter((p) => isValidCoord(p.lat, p.lng)), [points]);
  const [selectedPointIndex, setSelectedPointIndex] = useState<number | null>(null);
  const markerRefs = useRef<(L.Marker | null)[]>([]);

  const circles: GpsMapCircleWithLabel[] = useMemo(() => {
    if (circlesProp && circlesProp.length > 0) return circlesProp;
    if (circle && isValidCoord(circle.lat, circle.lng) && typeof circle.radiusMiles === "number") {
      return [{ ...circle, label: circleLabel }];
    }
    return [];
  }, [circlesProp, circle, circleLabel]);

  const validCircles = useMemo(
    () =>
      circles.filter(
        (c) => isValidCoord(c.lat, c.lng) && typeof c.radiusMiles === "number"
      ),
    [circles]
  );

  useEffect(() => {
    setSelectedPointIndex(null);
  }, [points]);

  const center: [number, number] = useMemo(() => {
    if (validCircles.length > 0) return [validCircles[0].lat, validCircles[0].lng];
    if (validPoints.length > 0) return [validPoints[0].lat, validPoints[0].lng];
    return DEFAULT_CENTER;
  }, [validCircles, validPoints]);

  if (!isOpen) return null;

  return (
    <div className="gps-map-modal-overlay">
      <div className="gps-map-modal">
        <div className="gps-map-modal-header">
          <h2>{title}</h2>
        </div>
        <div className="gps-map-modal-content">
          <div className="gps-map-container">
            <MapContainer
              center={center}
              zoom={DEFAULT_ZOOM}
              className="gps-map-inner"
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution={MAP_TILE_ATTRIBUTION}
                url={MAP_TILE_URL}
                maxZoom={MAP_TILE_MAX_ZOOM}
                referrerPolicy="strict-origin-when-cross-origin"
              />
              <FitBounds points={validPoints} circles={validCircles} />
              <FocusSelectedPin
                selectedPointIndex={selectedPointIndex}
                validPoints={validPoints}
                markerRefs={markerRefs}
              />
              {validCircles.map((c, idx) => (
                <Circle
                  key={idx}
                  center={[c.lat, c.lng]}
                  radius={c.radiusMiles * 1609.344}
                  pathOptions={{ color: "#3388ff", fillColor: "#3388ff", fillOpacity: 0.15, weight: 2 }}
                >
                  {c.label && (
                    <Popup>
                      <strong>{c.label}</strong>
                    </Popup>
                  )}
                </Circle>
              ))}
              {validPoints.map((p, i) => (
                  <Marker
                    key={i}
                    position={[p.lat, p.lng]}
                    icon={p.isException ? redPinIcon : bluePinIcon}
                    ref={(ref) => {
                      markerRefs.current[i] = ref as L.Marker | null;
                    }}
                  >
                    <Popup>
                      <strong>{p.label}</strong>
                      {p.isException && p.exceptionMessage && (
                        <div style={{ color: "#c0392b", marginTop: 4 }}>{p.exceptionMessage}</div>
                      )}
                    </Popup>
                  </Marker>
                ))}
            </MapContainer>
          </div>
          {showPunchList && validPoints.length > 0 && (
            <div className="gps-map-punch-list">
              <div className="gps-map-punch-list-title">Punches</div>
              <ul className="gps-map-punch-list-items">
                {validPoints.map((p, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      className={`gps-map-punch-list-item ${selectedPointIndex === i ? "gps-map-punch-list-item-selected" : ""} ${p.isException ? "gps-map-punch-list-item-exception" : ""}`}
                      onClick={() => setSelectedPointIndex(i)}
                    >
                      <span className="gps-map-punch-list-item-label">{p.label}</span>
                      {p.projectLabel && <span className="gps-map-punch-list-item-project">{p.projectLabel}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="gps-map-modal-actions">
          <ActionButton title="Close" onAction={onClose} isEnabled={true} />
        </div>
      </div>
    </div>
  );
};

export default GpsMapModal;
